import { useEffect, useRef, useState } from "react";

const MIN_THUMB_HEIGHT = 72;
const TRACK_INSET = 16;

type ModalScrollbarState = {
  thumbHeight: number;
  thumbOffset: number;
  visible: boolean;
};

const HIDDEN_SCROLLBAR: ModalScrollbarState = {
  thumbHeight: 0,
  thumbOffset: 0,
  visible: false,
};

export function useModalScrollbar(dependencies: readonly unknown[]) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollbar, setScrollbar] = useState<ModalScrollbarState>(HIDDEN_SCROLLBAR);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const updateScrollbar = () => {
      const { clientHeight, scrollHeight, scrollTop } = scrollElement;
      const trackHeight = clientHeight - TRACK_INSET * 2;

      if (trackHeight <= 0 || scrollHeight <= clientHeight + 1) {
        setScrollbar((current) => (current.visible ? HIDDEN_SCROLLBAR : current));
        return;
      }

      const thumbHeight = Math.min(
        trackHeight,
        Math.max((clientHeight / scrollHeight) * trackHeight, MIN_THUMB_HEIGHT),
      );
      const maxScrollTop = scrollHeight - clientHeight;
      const maxThumbOffset = trackHeight - thumbHeight;
      const thumbOffset = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbOffset : 0;

      setScrollbar({ thumbHeight, thumbOffset, visible: true });
    };

    const frameId = window.requestAnimationFrame(updateScrollbar);
    const resizeObserver = typeof ResizeObserver === "undefined"
      ? null
      : new ResizeObserver(updateScrollbar);

    resizeObserver?.observe(scrollElement);
    scrollElement.addEventListener("scroll", updateScrollbar, { passive: true });
    window.addEventListener("resize", updateScrollbar);

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      scrollElement.removeEventListener("scroll", updateScrollbar);
      window.removeEventListener("resize", updateScrollbar);
    };
  }, dependencies);

  return { scrollRef, scrollbar };
}
